import { getProductById, Brand, ProductById } from '@fe-sdk/cbx-products'
import type { ProductInfo } from './product-data'
import ProductStockService from '../stock'
import type { CbxConfigType } from '../../utils/page-config'
import { AkamaiApiHeaders } from '../../utils/api-headers'

export type ProductDataOrResponse<T = ProductInfo & { status?: number }> =
  T | { data?: ProductById; status: number }

export const getProductFromData = <T = ProductInfo & { status?: number }>(
  data: ProductDataOrResponse<T> | undefined
): T | undefined => {
  if (!data) {
    return undefined
  }
  if (typeof data === 'object' && 'data' in data && data.data) {
    const productData = 'data' in data.data ? data.data.data : data.data
    return {
      ...(productData as { data: object }),
      status: data.status
    } as T
  }
  return data as T
}

interface FetchProductData extends AkamaiApiHeaders {
  config: CbxConfigType
  key: string
  includeVariants: string
  includeRelationships: string
  includeOffline: string
  includeCrosslinked: string
  isMock?: boolean
}

interface FetchStockData extends AkamaiApiHeaders {
  config: CbxConfigType
  articleList: string[]
  includeVariants?: string
  includeRelationships?: string
  includeOffline?: string
  includeCrosslinked?: string
  isMock?: boolean
}

export const fetchProductData = async <T = ProductInfo>({
  config,
  key,
  includeVariants,
  includeRelationships,
  includeOffline,
  includeCrosslinked,
  isMock,
  customHeaders
}: FetchProductData): Promise<T | undefined> => {
  if (isMock) {
    const mockSdkResponse = {
      data: {
        data: {
          globalArticleCode: key,
          globalArticleName: 'Mock Product',
          variants: []
        }
      },
      status: 200
    }
    return getProductFromData<T>(mockSdkResponse as ProductDataOrResponse<T>)
  }

  const brandValue = config?.brand?.trim() ? config.brand.toUpperCase() : 'IQOS'

  const sdkResponse = await getProductById({
    productId: key,
    baseURL: config?.cbxUrl ?? '',
    brand: brandValue as Brand,
    channel: config?.channel ?? '',
    xChannel: config?.xChannel || 'WEBSITE',
    countryCode: config?.market?.toUpperCase() ?? '',
    market: config?.market ?? '',
    locale: config?.locale,
    includeVariants: includeVariants === 'true',
    includeRelationships: includeRelationships === 'true',
    includeOffline: includeOffline === 'true',
    includeCrossLinked: includeCrosslinked === 'true',
    touchpoint: config?.touchpoint,
    ...(customHeaders ? { customHeaders } : {})
  })

  return getProductFromData<T>(sdkResponse as ProductDataOrResponse<T>)
}
export const fetchStockData = async ({
  config,
  articleList,
  isMock,
  customHeaders
}: FetchStockData) => {
  return ProductStockService.getProductsStockData({
    cbxUrl: config?.cbxUrl ?? '',
    market: config?.market ?? '',
    channel: config?.channel ?? '',
    xChannel: config?.xChannel ?? '',
    loggerEnabled: config?.loggerEnabled ?? false,
    articleList,
    isMock: isMock || false,
    ...(customHeaders ? { customHeaders } : {})
  })
}
